import { ButtonWithSpinner, FormFieldControl, ToastCustom } from "@components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { signature_schema } from "../schema";
import {
	checklistAnswerPresigned,
	checklistAnswerSubmit,
	signatureUploadFile,
} from "../services";
import { useChecklistStore } from "../store";
import type { FileUri, ISignatureForm } from "../types";
import { ChecklistSuccess } from "./checklist-success";
import { StepHeader } from "./step-header";
import { SubmitReview } from "./submit-review";

export const ChecklistSignature = () => {
	const { answerData } = useChecklistStore((state) => state.checklist) || {};
	const checklistAnswer = answerData?.checklistAnswer;

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}, []);

	const { mutate, isPending, isSuccess } = useMutation({
		mutationKey: ["checklist:signature"],
		mutationFn: checklistAnswerSubmit,
		onError: (error) => {
			if (error?.message) {
				toast(<ToastCustom description={error?.message} title="Erro" />);
			}
		},
	});

	const {
		mutateAsync: mutateUploadFilesAsync,
		isPending: isPendingUploadFiles,
	} = useMutation({
		mutationKey: ["checklist:upload-files"],
		mutationFn: signatureUploadFile,
		onError: (error) => {
			if (error?.message) {
				toast(
					<ToastCustom
						description="Faça o registro da imagem e envie novamente."
						title="Falha ao carregar a imagem."
					/>
				);
			}
		},
	});

	const {
		mutateAsync: mutateAnswerPresignedAsync,
		isPending: isPendingPresigned,
	} = useMutation({
		mutationKey: ["checklist:answer-presigned-signature"],
		mutationFn: checklistAnswerPresigned,
		onError: (error) => {
			if (error?.message) {
				toast(<ToastCustom description={error?.message} title="Erro" />);
			}
		},
	});

	const isLoading = useMemo(
		() => isPending || isPendingPresigned || isPendingUploadFiles,
		[isPending, isPendingPresigned, isPendingUploadFiles]
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<ISignatureForm>({
		mode: "onSubmit",
		disabled: isLoading,
		resolver: zodResolver(signature_schema),
	});
	const onSubmit = async (data: ISignatureForm) => {
		const { signature } = data;
		if (!(checklistAnswer?.id && checklistAnswer?.answerToken && signature)) {
			return;
		}

		const response = await mutateAnswerPresignedAsync({
			answerId: checklistAnswer?.id,
			answerToken: checklistAnswer?.answerToken,
			files: [
				{
					fileName: signature.name,
					contentType: signature.type,
				},
			],
		});
		const { urls } = response;

		await mutateUploadFilesAsync({
			url: urls[0].uploadUrl,
			contentType: urls[0].contentType,
			file: signature,
		});

		mutate({
			answerId: checklistAnswer?.id,
			body: {
				answerToken: checklistAnswer?.answerToken,
				signature: urls[0].filePath,
			},
		});
	};

	if (isSuccess) {
		return <ChecklistSuccess />;
	}

	return (
		<form
			className="mx-auto flex min-h-dvh w-full flex-col gap-5 bg-secondary pt-5"
			onSubmit={handleSubmit(onSubmit)}
		>
			<StepHeader subtitle="Assinar o checklist" title="Assinatura" />

			<div className="mx-auto flex h-full w-full flex-col items-center gap-4 px-4">
				<Controller
					control={control}
					name="signature"
					render={({ field: { value, onChange } }) => (
						<FormFieldControl
							className="max-w-[456px]"
							error={errors.signature?.message as string}
							htmlFor={"signature"}
							isRequired={true}
							label="ASSINATURA"
							showIconError={false}
						>
							<SubmitReview
								error={!!errors.signature?.message}
								name="signature"
								onChange={onChange}
								value={value as FileUri}
							/>
						</FormFieldControl>
					)}
					rules={{
						required: {
							value: true,
							message: "Obrigatório",
						},
					}}
				/>
			</div>
			<div className="sticky bottom-0 left-0 mt-auto w-full border-primary border-t bg-secondary px-4 pt-4 pb-8 text-center">
				<ButtonWithSpinner
					className="max-w-[456px]"
					isLoading={isLoading}
					type="submit"
				>
					Finalizar checklist
				</ButtonWithSpinner>
			</div>
		</form>
	);
};
